import { useState } from 'react'
import { Button } from './Button'
import { Note } from './Note'
import { formatJobTime } from '@/screens/shared/jobFormatting'
import type { ActionId } from '@/annotations'
import type { components } from '@/api/generated/schema'

type JobNote = components['schemas']['JobNote']

/**
 * `div.panel` (`#rj-notes`, `Prototype_v2.html`) wrapping a real `panel-header`/`tools` ("Add a
 * note") and a list of `div.thread` → `note-thread` entries (`process/08-COMPONENT-CATALOGUE.md`),
 * each carrying a `span.who` → `note-author` ("**Never null** — no note is system-authored") and a
 * timestamp. Shared between `v-backupjob` and `v-restorejob` — one table backs both job kinds'
 * notes server-side (`JobNote`'s own doc comment), and the thread is drawn identically either way.
 *
 * `JobNote.author`/`created_at` are never invented client-side — every note this component ever
 * renders came from the server; the "Add a note" form only ever submits `body`, the one field
 * `JobNoteCreate` accepts. Edit/delete are not built — the contract has neither operation yet
 * (`JobNote.edited_at` is reserved, not wired to anything).
 */
export function NoteThread({ notes, onAdd, pending, addActionId }: { notes: readonly JobNote[]; onAdd: (body: string) => void; pending: boolean; addActionId?: ActionId }) {
  const [adding, setAdding] = useState(false)
  const [body, setBody] = useState('')

  return (
    <div data-cmp="panel" className="bg-panel border border-bd rounded-2">
      <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
        <span className="flex-1">Job notes</span>
        <div data-cmp="tools">
          <Button data-act={addActionId} ghost onClick={() => setAdding((v) => !v)}>
            Add a note
          </Button>
        </div>
      </div>
      <div className="p-5">
        {adding && (
          <div className="mb-5">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              className="block w-full border border-bd2 rounded-1 py-3 px-4 text-body-lg"
              placeholder="What happened, and what you did about it"
            />
            <div className="mt-3 flex gap-3">
              <Button
                data-act={addActionId}
                disabled={!body.trim() || pending}
                onClick={() => {
                  onAdd(body.trim())
                  setBody('')
                  setAdding(false)
                }}
              >
                Save note
              </Button>
              <Button
                data-act={undefined}
                ghost
                onClick={() => {
                  setAdding(false)
                  setBody('')
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        {notes.length === 0 && <Note>No notes on this job yet.</Note>}
        {notes.map((n) => (
          <div key={n.note_id} data-cmp="note-thread" className="border border-bd rounded-2 py-4 px-5 mb-3 text-body text-tx2 leading-normal">
            <div>
              <span data-cmp="note-author" className="font-semibold text-tx">
                {n.author.display_name}
              </span>{' '}
              <span className="text-small text-muted">
                {formatJobTime(n.created_at)}
                {n.edited_at ? ' · edited' : ''}
              </span>
            </div>
            <div className="mt-2">{n.body}</div>
          </div>
        ))}
        <Note>Notes are recorded against the job and are included in the exported report and in the audit log.</Note>
      </div>
    </div>
  )
}
